/**
 * Content Security Policy Utilities
 *
 * Generates Content Security Policy directives based on environment and usage context.
 * CSP is the primary defense against XSS attacks by controlling which resources
 * the browser is allowed to load and execute.
 */

import { config } from '../../../configurations/config';
import {
  getFirebaseDomains,
  getS3Domains,
} from '../../../configurations/domains.config';

/**
 * Generates Content Security Policy based on environment and usage context
 *
 * CSP is the primary defense against XSS attacks by controlling which resources
 * the browser is allowed to load and execute.
 */
export const getContentSecurityPolicy = (
  isDevelopment?: boolean,
  forMetaTag: boolean = false,
): string => {
  // Use LOCAL_DEV environment variable - if not provided, determine from env
  const isDevMode =
    isDevelopment !== undefined
      ? isDevelopment
      : process.env.REACT_APP_LOCAL_DEV === 'true';

  // Use the existing config's apiEndpoint - no duplication!
  const backendUrl = config.apiEndpoint;

  // Get Firebase domains
  const firebaseDomains = getFirebaseDomains();

  // Get S3 domains for file uploads/storage
  const s3Domains = getS3Domains();

  // Build connect-src array with all allowed endpoints
  const connectSrc = [
    "'self'",
    backendUrl, // Backend API from your existing config
    ...firebaseDomains, // Firebase authentication domains
    ...s3Domains, // AWS S3 storage domains
  ];

  // In development, allow WebSocket for HMR and additional dev servers
  if (isDevMode) {
    connectSrc.push('ws:', 'wss:', 'ws://localhost:*', 'wss://localhost:*');
  }

  // Add production domains from environment if specified
  const allowedDomains =
    process.env.REACT_APP_ALLOWED_DOMAINS?.split(',') || [];
  connectSrc.push(...allowedDomains);

  const basePolicy: Record<string, string[]> = {
    'default-src': ["'self'"], // Only load resources from same origin by default
    'script-src': [
      "'self'",
      "'unsafe-inline'", // Required for React and Material-UI inline styles
      'https://www.googletagmanager.com', // Google Tag Manager for analytics
      ...firebaseDomains.filter(
        (domain) =>
          // Only include domains that are needed for script loading
          domain.includes('apis.google.com') ||
          domain.includes('www.google.com') ||
          domain.includes('www.gstatic.com') ||
          domain.includes('www.googletagmanager.com') ||
          domain.includes('accounts.google.com') ||
          domain.includes('firebase.googleapis.com') ||
          domain.includes('www.googleapis.com'),
      ),
      ...(isDevMode ? ["'unsafe-eval'"] : []), // Allow eval() only in dev (HMR)
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Required for Material-UI and CSS-in-JS libraries
      'https://fonts.googleapis.com', // Google Fonts CSS
      ...firebaseDomains.filter(
        (domain) => domain.includes('accounts.google.com'), // Google Sign-In styles
      ),
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com', // Google Fonts files
      'data:', // Base64 encoded fonts
    ],
    'img-src': [
      "'self'",
      'data:', // Base64 images (profile pictures, compressed images)
      'blob:', // Camera captures and file uploads
      'https:', // External images (fighter photos, UFC assets, Google profile pics)
    ],
    'media-src': [
      "'self'",
      'blob:', // Camera/microphone access for profile pictures
    ],
    'connect-src': connectSrc,
    // child-src is required for Firebase auth popups
    'child-src': [
      "'self'",
      'https://accounts.google.com', // Google Sign-In popup
      ...firebaseDomains.filter(
        (domain) =>
          domain.includes('accounts.google.com') ||
          domain.includes('.firebaseapp.com') ||
          domain.includes('.web.app'),
      ),
    ],
    // frame-src is also needed for some Firebase auth flows
    'frame-src': [
      "'self'",
      'https://accounts.google.com', // Google Sign-In frame
      ...firebaseDomains.filter(
        (domain) =>
          domain.includes('accounts.google.com') ||
          domain.includes('.firebaseapp.com') ||
          domain.includes('.web.app'),
      ),
    ],
    'object-src': ["'none'"], // Block Flash and other plugins
    'base-uri': ["'self'"], // Prevent base tag hijacking
    'form-action': ["'self'"], // Only allow form submissions to same origin
    'upgrade-insecure-requests': isDevMode ? [] : [''], // HTTPS enforcement in production
  };

  // Add frame-ancestors only for HTTP headers (ignored in meta tags)
  if (!forMetaTag) {
    basePolicy['frame-ancestors'] = isDevMode
      ? ["'self'", 'https://accounts.google.com']
      : ["'none'"];
  }

  const cspString = Object.entries(basePolicy)
    .filter(([_, values]) => values.length > 0)
    .map(([directive, values]) => `${directive} ${values.join(' ')}`)
    .join('; ');

  return cspString;
};
